import { useState } from 'react';
import { Send, Instagram, Mail, DollarSign, ArrowLeft, CheckCircle, Phone, MessageCircle, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import usePageMeta from '../hooks/usePageMeta';

const API = process.env.REACT_APP_API_URL;

export default function ContactPage() {
  const { t, language } = useLanguage();
  usePageMeta('Kontakt | Lino', 'Projekt anfragen oder Kontakt aufnehmen — Webentwicklung, Automatisierung und IT-Dienstleistungen.');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagram: '',
    phone: '',
    budget: '',
    message: '',
    preferInstagram: false,
    phoneType: '' // 'whatsapp' | 'messenger' | 'anruf'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agbAccepted) return; // Absenden nur mit bestätigten AGB
    setIsSubmitting(true);

    try {
      // Backend speichert die Nachricht (primär)
      const res = await fetch(`${API}/api/contact-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          instagram: formData.instagram || null,
          phone: formData.phone || null,
          budget: formData.budget || null,
          message: formData.message,
          prefer_instagram: formData.preferInstagram,
          prefer_phone: !!formData.phoneType,
          phone_type: formData.phoneType || null
        })
      });

      if (!res.ok) throw new Error('Backend error');

      // E-Mail-Versand (an formsubmit.co) übernimmt das Backend — hier NICHT
      // nochmal senden, sonst kommt jede Anfrage doppelt per Mail an.

      setIsSuccess(true);
      setFormData({ name: '', email: '', instagram: '', phone: '', budget: '', message: '', preferInstagram: false, phoneType: '' });
      setAgbAccepted(false);
      setTimeout(() => setIsSuccess(false), 5000);

    } catch (error) {
      console.error('Error:', error);
      alert(language === 'de'
        ? 'Fehler beim Senden. Bitte versuche es später erneut.'
        : 'Error sending message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link 
            to="/" 
            className="icon-button" 
            style={{ 
              display: 'inline-flex', 
              marginBottom: '32px',
              gap: '8px',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={20} />
            <span style={{ fontWeight: 500 }}>Back</span>
          </Link>

          <AnimatePresence>
            {isSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                style={{
                  marginBottom: '32px',
                  padding: '20px',
                  background: 'var(--accent)',
                  color: '#000',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontWeight: 600
                }}
              >
                <CheckCircle size={24} />
                {language === 'de'
                  ? 'Nachricht erfolgreich gesendet! Ich melde mich bald bei dir.'
                  : 'Message sent successfully! I\'ll get back to you soon.'}
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h1 className="section-title">{t('contact.title')}</h1>
            <p className="section-subtitle">{t('contact.subtitle')}</p>
          </div>
          
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                {t('contact.name')} *
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder={language === 'de' ? 'Max Mustermann' : 'John Doe'}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <Mail size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                {t('contact.email')} *
              </label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder={language === 'de' ? 'max@example.com' : 'john@example.com'}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <Instagram size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Instagram <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 400 }}>(Optional)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)',
                  fontSize: '16px'
                }}>@</span>
                <input
                  type="text"
                  className="form-input"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value.replace('@', '') })}
                  placeholder="username"
                  style={{ paddingLeft: '36px' }}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <Phone size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Telefon <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 400 }}>(Optional)</span>
              </label>
              <input
                type="tel"
                className="form-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9+]/g, '') })}
                placeholder="+49 123 456789"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <DollarSign size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Budget <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 400 }}>(Optional)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: '16px', top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)', fontSize: '15px', pointerEvents: 'none'
                }}>€</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value.replace(/[^0-9]/g, '') })}
                  placeholder="500"
                  style={{ paddingLeft: '36px' }}
                />
              </div>
            </div>
            
            {(formData.instagram || formData.phone) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  {language === 'de' ? 'Wie soll ich mich bevorzugt melden?' : 'How should I best reach you?'}
                </p>

                {/* Instagram option */}
                {formData.instagram && (
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer',
                    padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px',
                    border: `2px solid ${formData.preferInstagram ? 'var(--accent)' : 'var(--border)'}`,
                    transition: 'all 0.2s'
                  }}>
                    <input type="checkbox" checked={formData.preferInstagram}
                      onChange={(e) => setFormData({ ...formData, preferInstagram: e.target.checked, phoneType: e.target.checked ? '' : formData.phoneType })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--accent)' }} />
                    <Instagram size={18} style={{ color: 'var(--accent)' }} />
                    <span style={{ fontWeight: 500, flex: 1, fontSize: '14px' }}>Instagram</span>
                  </label>
                )}

                {/* Phone type chips */}
                {formData.phone && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { key: 'whatsapp',  icon: <MessageCircle size={15} />, label: 'WhatsApp' },
                      { key: 'messenger', icon: <MessageSquare size={15} />, label: 'Messenger' },
                      { key: 'anruf',     icon: <Phone size={15} />,         label: 'Anruf' }
                    ].map(({ key, icon, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          phoneType: formData.phoneType === key ? '' : key,
                          preferInstagram: formData.phoneType === key ? formData.preferInstagram : false
                        })}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '7px',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: `2px solid ${formData.phoneType === key ? 'var(--accent)' : 'var(--border)'}`,
                          background: formData.phoneType === key ? 'rgba(251,146,60,0.1)' : 'var(--bg-secondary)',
                          color: formData.phoneType === key ? 'var(--accent)' : 'var(--text-secondary)',
                          fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {icon}{label}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
            
            <div className="form-group">
              <label className="form-label">
                {t('contact.message')} *
              </label>
              <textarea
                className="form-textarea"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                placeholder={language === 'de' ? 'Erzähl mir von deinem Projekt...' : 'Tell me about your project...'}
                style={{ minHeight: '180px' }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                <input
                  type="checkbox"
                  checked={agbAccepted}
                  onChange={(e) => setAgbAccepted(e.target.checked)}
                  required
                  style={{ width: '18px', height: '18px', marginTop: '1px', cursor: 'pointer', accentColor: 'var(--accent)', flexShrink: 0 }}
                />
                <span>
                  {language === 'de' ? 'Ich habe die ' : 'I have read and accept the '}
                  <Link to="/agb" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>AGB</Link>
                  {language === 'de' ? ' gelesen und akzeptiere sie.' : '.'}
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !agbAccepted}
              style={{
                opacity: (isSubmitting || !agbAccepted) ? 0.55 : 1,
                cursor: (isSubmitting || !agbAccepted) ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: '3px solid #000', 
                    borderTop: '3px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {language === 'de' ? 'Wird gesendet...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send size={20} />
                  {t('contact.send')}
                </>
              )}
            </button>
          </form>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              marginTop: '48px',
              padding: '24px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              textAlign: 'center'
            }}
          >
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Mail size={14} />
              {language === 'de'
                ? 'Deine Nachricht wird direkt an mich gesendet.'
                : 'Your message will be sent directly to me.'}
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Instagram size={14} />
              {language === 'de'
                ? 'Wenn du Instagram bevorzugst, melde ich mich dort bei dir!'
                : 'If you prefer Instagram, I\'ll reach out to you there!'}
            </p>
          </motion.div>
        </motion.div>
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}